import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Flame, Menu, X, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AuthDialog from "./AuthDialog";
import Footer from "./Footer";
import { Button } from "./ui/button";
import { toast } from "sonner";

const links = [
  { to: "/knowledge", label: "NFPA Codes" },
  { to: "/training", label: "Training" },
  { to: "/map", label: "Map" },
  { to: "/forum", label: "Forum" },
  { to: "/directory", label: "Directory" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <div className="App flex flex-col min-h-screen">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <div className="leading-none">
              <span className="font-display font-black text-xl tracking-tighter">FIRE</span>
              <span className="block label-tech text-muted-foreground text-[0.6rem]">Sprinkler Network</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "text-primary" : "text-foreground hover:text-primary"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user && user.role === "admin" && (
              <Button asChild variant="outline" className="rounded-none border-foreground gap-1" data-testid="nav-admin">
                <Link to="/admin"><ShieldCheck className="w-4 h-4" /> Admin</Link>
              </Button>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold" data-testid="nav-user-name">{user.name?.split(" ")[0]}</span>
                <Button onClick={doLogout} variant="ghost" size="icon" className="rounded-none" data-testid="nav-logout">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button onClick={() => setAuthOpen(true)} className="rounded-none bg-primary hover:bg-[#D62828] font-bold" data-testid="nav-signin">
                Member Login
              </Button>
            )}
          </div>

          <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-white px-4 py-3 space-y-2">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">
                {l.label}
              </NavLink>
            ))}
            {user && user.role === "admin" && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Admin</Link>}
            {user ? (
              <Button onClick={doLogout} className="w-full rounded-none" variant="outline">Sign Out</Button>
            ) : (
              <Button onClick={() => { setAuthOpen(true); setMobileOpen(false); }} className="w-full rounded-none bg-primary">Member Login</Button>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet context={{ openAuth: () => setAuthOpen(true) }} />
      </main>

      <Footer />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
